import React from 'react';
import { Section } from './ui/Section';
import { motion } from 'framer-motion';
import { MessageCircleIcon, CheckCircleIcon } from 'lucide-react';
interface Review {
  name: string;
  initial: string;
  color: 'forest' | 'gold';
  time: string;
  message: string;
}
function ChatBubble({
  review,
  index
}: {
  review: Review;
  index: number;
}) {
  const avatarBg = review.color === 'forest' ? 'bg-[#1B4332]' : 'bg-[#C9963A]';
  return <motion.div initial={{
    opacity: 0,
    x: -20,
    y: 10
  }} whileInView={{
    opacity: 1,
    x: 0,
    y: 0
  }} viewport={{
    once: true,
    margin: '-30px'
  }} transition={{
    duration: 0.4,
    delay: index * 0.1,
    ease: 'easeOut'
  }} className="flex items-start gap-3" data-id="element-216">
      {/* Avatar */}
      <div className={`${avatarBg} w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-1`} data-id="element-217">
        <span className="text-white font-bold text-sm" data-id="element-218">{review.initial}</span>
      </div>

      {/* Bubble */}
      <div className="flex-1 max-w-lg" data-id="element-219">
        <div className="bg-white border border-[#1B4332]/8 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm" data-id="element-220">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1.5" data-id="element-221">
            <span className="font-bold text-[#1B4332] text-sm" data-id="element-222">
              {review.name}
            </span>
            <CheckCircleIcon className="w-3.5 h-3.5 text-[#2D6A4F]" data-id="element-223" />
            <span className="text-xs text-gray-400 ml-auto" data-id="element-224">{review.time}</span>
          </div>

          {/* Message */}
          <p className="text-gray-700 text-sm leading-relaxed" data-id="element-225">
            {review.message}
          </p>

          {/* Stars */}
          <div className="mt-2 flex gap-0.5" data-id="element-226">
            <span className="text-[#C9963A] text-xs" data-id="element-227">★</span>
            <span className="text-[#C9963A] text-xs" data-id="element-228">★</span>
            <span className="text-[#C9963A] text-xs" data-id="element-229">★</span>
            <span className="text-[#C9963A] text-xs" data-id="element-230">★</span>
            <span className="text-[#C9963A] text-xs" data-id="element-231">★</span>
          </div>
        </div>
      </div>
    </motion.div>;
}
export function TestimonialsSection() {
  return <Section background="white" data-id="element-232">
      <div className="text-center mb-8" data-id="element-233">
        <div className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-4 py-1 rounded-full text-sm font-medium mb-4" data-id="element-234">
          <MessageCircleIcon className="w-4 h-4" data-id="element-235" />
          +230 personas ya lo usan
        </div>
        <h2 className="text-3xl md:text-4xl mb-3" data-id="element-236">
          "Ojalá lo hubiera tenido antes"
        </h2>
        <p className="text-gray-600 max-w-xl mx-auto" data-id="element-237">
          Estos mensajes nos llegan todos los días. Los compartimos con permiso.
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-4" data-id="element-238">
        <ChatBubble index={0} review={{
        name: 'Carolina M.',
        initial: 'C',
        color: 'forest',
        time: 'Hace 3 días',
        message: 'Eran las 11 de la noche, mi nena con dolor de garganta, y yo googleando como loca. Encontraba de todo: "es grave", "no es nada", "tomá esto", "no tomes eso". Desde que tengo el botiquín, abro la ficha y en 2 minutos sé qué hacer. Se acabó la angustia nocturna.'
      }} data-id="element-239" />

        <ChatBubble index={1} review={{
        name: 'Martín R.',
        initial: 'M',
        color: 'gold',
        time: 'Hace 1 semana',
        message: 'Lo del Semáforo de Riesgo es genial. Mi suegra tenía una tos que no se iba y yo no sabía si era para preocuparse. Miré el semáforo: amarillo. La llevamos al médico a tiempo. Esa tranquilidad de saber cuándo sí y cuándo no, no tiene precio.'
      }} data-id="element-240" />

        <ChatBubble index={2} review={{
        name: 'Lucía P.',
        initial: 'L',
        color: 'forest',
        time: 'Hace 5 días',
        message: 'El bono de Aromaterapia Fácil me voló la cabeza. Pensé que iba a ser algo básico y terminé haciéndome mis propios aceites en casa. Ahora tengo lavanda y eucalipto caseros que uso para todo. ¡Un regalo increíble!'
      }} data-id="element-241" />

        <ChatBubble index={3} review={{
        name: 'Valeria S.',
        initial: 'V',
        color: 'gold',
        time: 'Hace 2 semanas',
        message: 'Soy mamá de dos y cada vez que les subía la fiebre me paralizaba. No sabía si darles algo, si esperar, si correr a la guardia. Ahora tengo la ficha pegada en la heladera. Literalmente: "fiebre → hago esto → si pasa esto otro, voy al médico". Clarísimo.'
      }} data-id="element-242" />

        <ChatBubble index={4} review={{
        name: 'Diego F.',
        initial: 'D',
        color: 'forest',
        time: 'Hace 4 días',
        message: 'Antes gastaba plata en cualquier cosa que me recomendaban: un té por acá, unas gotas por allá, suplementos que ni sabía para qué eran. Con la lista de compras del botiquín armé mi kit real por menos de lo que gastaba en un mes de improvisar.'
      }} data-id="element-243" />

        <ChatBubble index={5} review={{
        name: 'Romina G.',
        initial: 'R',
        color: 'gold',
        time: 'Hace 1 semana',
        message: 'Las fichas imprimibles son lo mejor que tiene. Las tengo pegadas en la heladera y en el botiquín del baño. Mi marido, que no entiende nada de plantas, las usó solo cuando le dolía el estómago. Si él pudo, cualquiera puede.'
      }} data-id="element-244" />

        <ChatBubble index={6} review={{
        name: 'Andrés T.',
        initial: 'A',
        color: 'forest',
        time: 'Hace 3 días',
        message: 'El Protocolo Express por Síntoma me cambió la vida. "Me pasa esto → hago esto". Así de simple. Ya no pierdo 40 minutos buscando en internet. Abro la guía, sigo los pasos, y listo. Me siento con el control que antes no tenía.'
      }} data-id="element-245" />
      </div>
    </Section>;
}